import { useState, useMemo } from 'react';
import { Flame, Award, Zap, TrendingUp } from 'lucide-react';
import { theme } from '../../lib/theme';

/**
 * LeaderboardWidget - Agent Gamification Dashboard
 * Displays ranked agents with composite scoring, streaks, and badges.
 * Props: { agents, setActiveTab }
 */
export default function LeaderboardWidget({ agents = [], setActiveTab }) {
  // Demo data fallback: Trovva team members
  const DEMO_AGENTS = [
    { name: 'Patricia Furrow', score: 94, dealsClosedMTD: 3, pipelineValue: 1800000, streak: 12, daysActive: 12, avgResponseTime: 8 },
    { name: 'Sylvia Micarelli', score: 87, dealsClosedMTD: 2, pipelineValue: 1300000, streak: 5, daysActive: 5, avgResponseTime: 15 },
    { name: 'Jenifer Raffa', score: 85, dealsClosedMTD: 2, pipelineValue: 875000, streak: 3, daysActive: 3, avgResponseTime: 22 },
    { name: 'Desiree Hogbin', score: 78, dealsClosedMTD: 1, pipelineValue: 620000, streak: 1, daysActive: 1, avgResponseTime: 35 },
    { name: 'Neha', score: 72, dealsClosedMTD: 1, pipelineValue: 340000, streak: 0, daysActive: 0, avgResponseTime: 45 },
  ];

  const displayAgents = agents && agents.length > 0 ? agents : DEMO_AGENTS;

  // Rank agents by composite score (higher is better)
  const rankedAgents = useMemo(() => {
    return [...displayAgents]
      .sort((a, b) => (b.score || 0) - (a.score || 0))
      .map((agent, idx) => ({
        ...agent,
        rank: idx + 1,
        medal: idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : null,
        badges: [
          agent.streak >= 5 ? 'Hot Streak' : null,
          Math.max(...displayAgents.map(a => a.pipelineValue || 0)) === (agent.pipelineValue || 0) ? 'Pipeline King' : null,
          Math.min(...displayAgents.map(a => a.avgResponseTime || 999)) === (agent.avgResponseTime || 999) ? 'Speed Demon' : null,
        ].filter(Boolean),
      }));
  }, [displayAgents]);

  const formatCurrency = (val) => {
    if (!val) return '$0';
    if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
    if (val >= 1_000) return `$${(val / 1_000).toFixed(0)}K`;
    return `$${val}`;
  };

  return (
    <div
      data-tour="leaderboard"
      style={{
        background: theme.colors.card,
        borderRadius: 12,
        border: `1px solid ${theme.colors.border}`,
        overflow: 'hidden',
        marginBottom: 18,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '12px 16px',
          borderBottom: `1px solid ${theme.colors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Award size={14} color={theme.colors.accent} />
          <span style={{ fontSize: 13, fontWeight: 700, color: theme.colors.text }}>Agent Leaderboard</span>
        </div>
        <button
          onClick={() => setActiveTab?.('activity')}
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: theme.colors.primary,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 3,
          }}
        >
          All agents →
        </button>
      </div>

      {/* Table Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '8px 16px',
          background: theme.colors.bg,
          borderBottom: `1px solid ${theme.colors.border}`,
        }}
      >
        <div
          style={{
            width: 30,
            fontSize: 9,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color: theme.colors.textLight,
          }}
        >
          Rank
        </div>
        <div
          style={{
            flex: 1,
            fontSize: 9,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color: theme.colors.textLight,
          }}
        >
          Agent
        </div>
        <div
          style={{
            width: 50,
            textAlign: 'center',
            fontSize: 9,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color: theme.colors.textLight,
          }}
        >
          Score
        </div>
        <div
          style={{
            width: 60,
            textAlign: 'center',
            fontSize: 9,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color: theme.colors.textLight,
          }}
        >
          Closed
        </div>
        <div
          style={{
            width: 70,
            textAlign: 'center',
            fontSize: 9,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color: theme.colors.textLight,
          }}
        >
          Pipeline
        </div>
        <div
          style={{
            width: 80,
            textAlign: 'center',
            fontSize: 9,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color: theme.colors.textLight,
          }}
        >
          Streak
        </div>
      </div>

      {/* Table Rows */}
      <div>
        {rankedAgents.length === 0 ? (
          <div style={{ padding: '28px 20px', textAlign: 'center' }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: `${theme.colors.primary}10`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 10px',
              }}
            >
              <TrendingUp size={20} color={theme.colors.primary} />
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text, marginBottom: 4 }}>
              No agents yet
            </div>
            <div style={{ fontSize: 12, color: theme.colors.textLight }}>
              Agent data will appear here as activity is tracked.
            </div>
          </div>
        ) : (
          rankedAgents.map((agent, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '11px 16px',
                borderBottom: i < rankedAgents.length - 1 ? `1px solid ${theme.colors.border}40` : 'none',
                background: agent.rank <= 3 ? `${theme.colors.accent}08` : 'transparent',
              }}
            >
              {/* Rank & Medal */}
              <div
                style={{
                  width: 30,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {agent.medal ? (
                  <span style={{ fontSize: 16 }}>{agent.medal}</span>
                ) : (
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: theme.colors.textLight,
                    }}
                  >
                    #{agent.rank}
                  </span>
                )}
              </div>

              {/* Agent Name + Badges */}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: theme.colors.text, marginBottom: 3 }}>
                  {agent.name}
                </div>
                {agent.badges.length > 0 && (
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {agent.badges.map((badge, bi) => (
                      <span
                        key={bi}
                        style={{
                          fontSize: 9,
                          fontWeight: 600,
                          padding: '2px 6px',
                          borderRadius: 4,
                          background:
                            badge === 'Hot Streak'
                              ? `${theme.colors.accent}15`
                              : badge === 'Pipeline King'
                                ? `${theme.colors.success}15`
                                : `${theme.colors.primary}15`,
                          color:
                            badge === 'Hot Streak'
                              ? theme.colors.accent
                              : badge === 'Pipeline King'
                                ? theme.colors.success
                                : theme.colors.primary,
                        }}
                      >
                        {badge === 'Hot Streak' && '🔥'} {badge}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Score */}
              <div
                style={{
                  width: 50,
                  textAlign: 'center',
                  fontSize: 12,
                  fontWeight: 700,
                  color: agent.rank <= 3 ? theme.colors.accent : theme.colors.primary,
                }}
              >
                {agent.score}
              </div>

              {/* Deals Closed */}
              <div
                style={{
                  width: 60,
                  textAlign: 'center',
                  fontSize: 12,
                  fontWeight: 600,
                  color: theme.colors.success,
                }}
              >
                {agent.dealsClosedMTD || 0}
              </div>

              {/* Pipeline */}
              <div
                style={{
                  width: 70,
                  textAlign: 'center',
                  fontSize: 11,
                  fontWeight: 600,
                  color: theme.colors.text,
                }}
              >
                {formatCurrency(agent.pipelineValue)}
              </div>

              {/* Streak */}
              <div
                style={{
                  width: 80,
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 3,
                }}
              >
                {agent.streak > 0 ? (
                  <>
                    <Flame size={12} color={theme.colors.accent} />
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: theme.colors.accent,
                      }}
                    >
                      {agent.streak}d
                    </span>
                  </>
                ) : (
                  <span style={{ fontSize: 11, color: theme.colors.textLight }}>—</span>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
